import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { api, DirectoryConnection, DirectorySyncLog, DirectoryPerson } from '../lib/api'
import { useToast } from '../components/Toast'
import { useConfirm } from '../components/ConfirmModal'
import {
  Cloud,
  Plus,
  RefreshCw,
  Settings,
  Trash2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  ExternalLink,
  Users,
  Server,
  Shield,
  Database,
  HardDrive,
  HelpCircle,
  Webhook,
  Play,
  Edit,
  X,
  Save,
  Plug,
  Link2,
  Upload,
  ChevronDown,
  ChevronUp,
  Clock,
  UserPlus,
  Search,
  Building2,
  Mail,
  ArrowUpCircle,
  MoreVertical,
} from 'lucide-react'
import AtlassianIntegration from '../components/AtlassianIntegration'
import CsvImportModal from '../components/CsvImportModal'

// ============= AWS Regions =============

const AWS_REGIONS = [
  { value: 'all', label: 'All Regions (Multi-Region Scan)' },
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'us-east-2', label: 'US East (Ohio)' },
  { value: 'us-west-1', label: 'US West (N. California)' },
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'eu-west-1', label: 'EU (Ireland)' },
  { value: 'eu-west-2', label: 'EU (London)' },
  { value: 'eu-west-3', label: 'EU (Paris)' },
  { value: 'eu-central-1', label: 'EU (Frankfurt)' },
  { value: 'eu-north-1', label: 'EU (Stockholm)' },
  { value: 'ap-south-1', label: 'Asia Pacific (Mumbai)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
  { value: 'ap-northeast-2', label: 'Asia Pacific (Seoul)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
  { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
  { value: 'ca-central-1', label: 'Canada (Central)' },
  { value: 'sa-east-1', label: 'South America (Sao Paulo)' },
]

// ============= Types =============

type Integration = {
  id: string
  name: string
  type: 'aws' | 'azure' | 'gcp'
  enabled: boolean
  config: any
  last_sync_at: number | null
  last_sync_status: string | null
  last_sync_error: string | null
  last_sync_records: number | null
  sync_frequency: string
  created_at: number
}

type WebhookType = {
  id: string
  name: string
  description: string | null
  url: string
  auth_type: 'none' | 'hmac' | 'bearer' | 'basic'
  events: string[]
  enabled: number
  success_count: number
  failure_count: number
  last_triggered_at: number | null
  last_success_at: number | null
  created_at: number
}

type SubTab = 'cloud' | 'webhooks' | 'atlassian' | 'directory'

// ============= Cloud Provider Info =============

const providerInfo = {
  aws: {
    name: 'Amazon Web Services',
    logo: '/aws-logo.svg',
    color: 'bg-orange-500',
    description: 'Collect IAM users/roles/policies, EC2 instances, Security Groups, and RDS databases',
    dataTypes: [
      { name: 'IAM Users & Roles', icon: Users, description: 'User accounts, roles, and access policies' },
      { name: 'EC2 Instances', icon: Server, description: 'Virtual machine inventory' },
      { name: 'Security Groups', icon: Shield, description: 'Firewall rules and network access' },
      { name: 'RDS Databases', icon: Database, description: 'Database instances and configurations' },
    ],
  },
  azure: {
    name: 'Microsoft Azure',
    logo: '/azure-logo.svg',
    color: 'bg-blue-500',
    description: 'Collect Entra ID users/groups, VMs, NSGs, SQL databases, and Storage Accounts',
    dataTypes: [
      { name: 'Entra ID Users & Groups', icon: Users, description: 'Azure AD identities and groups' },
      { name: 'Virtual Machines', icon: Server, description: 'VM inventory and configurations' },
      { name: 'Network Security Groups', icon: Shield, description: 'NSG rules and network access' },
      { name: 'SQL Databases', icon: Database, description: 'Azure SQL databases' },
      { name: 'Storage Accounts', icon: HardDrive, description: 'Blob storage configurations' },
    ],
  },
  gcp: {
    name: 'Google Cloud Platform',
    logo: '/gcp-logo.svg',
    color: 'bg-red-500',
    description: 'GCP integration coming soon',
    dataTypes: [
      { name: 'IAM Policies', icon: Users, description: 'Coming soon' },
      { name: 'Compute Instances', icon: Server, description: 'Coming soon' },
      { name: 'Firewall Rules', icon: Shield, description: 'Coming soon' },
    ],
  },
}

// ============= Main Component =============

export default function Integrations() {
  const [subTab, setSubTab] = useState<SubTab>('cloud')

  // Cloud integrations state
  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [cloudLoading, setCloudLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null)
  const [syncingId, setSyncingId] = useState<string | null>(null)
  const [testingId, setTestingId] = useState<string | null>(null)
  const [syncResult, setSyncResult] = useState<{ id: string; records: number; evidence: number; error?: string } | null>(null)

  // Azure preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewData, setPreviewData] = useState<any>(null)
  const [previewIntegrationId, setPreviewIntegrationId] = useState<string | null>(null)

  // Webhooks state
  const [webhooks, setWebhooks] = useState<WebhookType[]>([])
  const [webhooksLoading, setWebhooksLoading] = useState(true)
  const [webhookEvents, setWebhookEvents] = useState<Array<{ type: string; description: string; category: string }>>([])
  const [showWebhookModal, setShowWebhookModal] = useState(false)
  const [editingWebhook, setEditingWebhook] = useState<WebhookType | null>(null)
  const [testingWebhook, setTestingWebhook] = useState<string | null>(null)
  const [deletingWebhook, setDeletingWebhook] = useState<string | null>(null)
  const [webhookSaving, setWebhookSaving] = useState(false)
  const [webhookForm, setWebhookForm] = useState({
    name: '',
    description: '',
    url: '',
    secret: '',
    auth_type: 'hmac' as 'none' | 'hmac' | 'bearer' | 'basic',
    events: [] as string[],
    enabled: true,
  })

  // Directory connections state
  const [directoryConnections, setDirectoryConnections] = useState<DirectoryConnection[]>([])
  const [directoryLoading, setDirectoryLoading] = useState(true)
  const [syncLogs, setSyncLogs] = useState<DirectorySyncLog[]>([])
  const [showSyncLogs, setShowSyncLogs] = useState(false)
  const [syncingDirectory, setSyncingDirectory] = useState<string | null>(null)

  // Directory people state
  const [directoryPeople, setDirectoryPeople] = useState<DirectoryPerson[]>([])
  const [peopleLoading, setPeopleLoading] = useState(false)
  const [showPeople, setShowPeople] = useState(true)
  const [peopleFilter, setPeopleFilter] = useState<{ search: string; source: string; department: string }>({
    search: '',
    source: '',
    department: ''
  })
  const [departments, setDepartments] = useState<Array<{ department: string; person_count: number }>>([])
  const [peopleStats, setPeopleStats] = useState<{ by_status: Array<{ status: string; count: number }>; by_source: Array<{ directory_source: string; count: number }> } | null>(null)
  const [showPromoteModal, setShowPromoteModal] = useState(false)
  const [personToPromote, setPersonToPromote] = useState<DirectoryPerson | null>(null)
  const [promotingPerson, setPromotingPerson] = useState(false)
  const [showCsvModal, setShowCsvModal] = useState(false)
  const [connectingProvider, setConnectingProvider] = useState<string | null>(null)

  // OAuth Configuration Modal state
  const [showOAuthConfigModal, setShowOAuthConfigModal] = useState(false)
  const [oauthConfigProvider, setOAuthConfigProvider] = useState<'google' | 'entra' | null>(null)
  const [oauthConfigForm, setOAuthConfigForm] = useState({
    clientId: '',
    clientSecret: '',
    entraTenantId: '', // Only for Entra
  })
  const [oauthConfigSaving, setOAuthConfigSaving] = useState(false)

  const toast = useToast()
  const confirmDialog = useConfirm()

  // ============= Data Loading =============

  useEffect(() => {
    loadIntegrations()
    loadWebhooks()
    loadDirectoryConnections()
  }, [])

  // Load directory people when tab is selected or filters change
  useEffect(() => {
    if (subTab === 'directory') {
      loadDirectoryPeople()
    }
  }, [subTab, peopleFilter.source, peopleFilter.department])

  const loadIntegrations = async () => {
    try {
      const response = await api.getIntegrations()
      if (response.success) {
        setIntegrations(response.integrations)
      }
    } catch (error) {
      console.error('Failed to load integrations:', error)
    } finally {
      setCloudLoading(false)
    }
  }

  const loadWebhooks = async () => {
    try {
      const [webhooksRes, eventsRes] = await Promise.all([
        api.getWebhooks(),
        api.getWebhookEvents(),
      ])
      if (webhooksRes.success) {
        setWebhooks(webhooksRes.webhooks as WebhookType[])
      }
      if (eventsRes.success) {
        setWebhookEvents(eventsRes.events)
      }
    } catch (error) {
      console.error('Failed to load webhooks:', error)
    } finally {
      setWebhooksLoading(false)
    }
  }

  const loadDirectoryConnections = async () => {
    try {
      const [connectionsRes, logsRes] = await Promise.all([
        api.getDirectoryConnections(),
        api.getDirectorySyncLogs({ limit: 20 }),
      ])
      if (connectionsRes.success) {
        setDirectoryConnections(connectionsRes.data)
      }
      if (logsRes.success) {
        setSyncLogs(logsRes.data)
      }
    } catch (error) {
      console.error('Failed to load directory connections:', error)
    } finally {
      setDirectoryLoading(false)
    }
  }

  const loadDirectoryPeople = async () => {
    setPeopleLoading(true)
    try {
      const params: { source?: 'manual' | 'csv' | 'google' | 'entra'; department?: string; search?: string } = {}
      if (peopleFilter.source) params.source = peopleFilter.source as 'manual' | 'csv' | 'google' | 'entra'
      if (peopleFilter.department) params.department = peopleFilter.department
      if (peopleFilter.search) params.search = peopleFilter.search

      const [peopleRes, statsRes, deptsRes] = await Promise.all([
        api.getDirectoryPeople({ ...params, limit: 100 }),
        api.getDirectoryPeopleStats(),
        api.getDirectoryPeopleDepartments()
      ])

      if (peopleRes.success) {
        setDirectoryPeople(peopleRes.data)
      }
      if (statsRes.success) {
        setPeopleStats(statsRes.stats)
      }
      if (deptsRes.success) {
        setDepartments(deptsRes.departments)
      }
    } catch (error) {
      console.error('Failed to load directory people:', error)
    } finally {
      setPeopleLoading(false)
    }
  }

  // ============= Directory Handlers =============

  // Open OAuth configuration modal instead of directly connecting
  const handleConnectDirectory = (provider: 'google' | 'entra') => {
    setOAuthConfigProvider(provider)
    setOAuthConfigForm({ clientId: '', clientSecret: '', entraTenantId: '' })
    setShowOAuthConfigModal(true)
  }

  // Save OAuth credentials and start OAuth flow
  const handleSaveOAuthConfig = async () => {
    if (!oauthConfigProvider) return

    if (!oauthConfigForm.clientId || !oauthConfigForm.clientSecret) {
      toast.error('Client ID and Client Secret are required')
      return
    }

    setOAuthConfigSaving(true)
    try {
      const name = oauthConfigProvider === 'google' ? 'Google Workspace' : 'Microsoft Entra ID'

      // Create connection with OAuth credentials
      const result = await api.createDirectoryConnection({
        name,
        provider: oauthConfigProvider,
        client_id: oauthConfigForm.clientId,
        client_secret: oauthConfigForm.clientSecret,
        config: oauthConfigProvider === 'entra' ? { entra_tenant_id: oauthConfigForm.entraTenantId || 'common' } : undefined,
      })

      if (result.success && result.oauth_url) {
        // Redirect to OAuth flow
        setShowOAuthConfigModal(false)
        window.location.href = result.oauth_url
      } else if (result.success) {
        toast.success(`${name} connection created`)
        setShowOAuthConfigModal(false)
        await loadDirectoryConnections()
      } else {
        toast.error('Failed to create connection')
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to connect')
    } finally {
      setOAuthConfigSaving(false)
    }
  }

  // Legacy handler for backward compatibility (used internally)
  const handleConnectDirectoryLegacy = async (provider: 'google' | 'entra') => {
    setConnectingProvider(provider)
    try {
      const name = provider === 'google' ? 'Google Workspace' : 'Microsoft Entra ID'
      const result = await api.createDirectoryConnection({
        name,
        provider,
      })
      if (result.success && result.oauth_url) {
        // Redirect to OAuth flow
        window.location.href = result.oauth_url
      } else if (result.success) {
        toast.success(`${name} connection created`)
        await loadDirectoryConnections()
      } else {
        toast.error('Failed to create connection')
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to connect')
    } finally {
      setConnectingProvider(null)
    }
  }

  const handleDisconnectDirectory = async (connection: DirectoryConnection) => {
    const confirmed = await confirmDialog({
      title: 'Disconnect Directory',
      message: `Are you sure you want to disconnect "${connection.name}"? Users synced from this directory will not be automatically removed.`,
      type: 'danger',
      confirmText: 'Disconnect',
    })
    if (!confirmed) return

    try {
      await api.deleteDirectoryConnection(connection.id)
      toast.success('Directory disconnected')
      await loadDirectoryConnections()
    } catch (error) {
      toast.error('Failed to disconnect directory')
    }
  }

  const handleDirectorySync = async (connection: DirectoryConnection) => {
    setSyncingDirectory(connection.id)
    try {
      const result = await api.triggerDirectorySync(connection.id)
      if (result.success) {
        toast.success('Sync completed successfully')
        await loadDirectoryConnections()
      } else {
        toast.error(result.message || 'Sync failed')
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Sync failed')
    } finally {
      setSyncingDirectory(null)
    }
  }

  const handleToggleDirectorySync = async (connection: DirectoryConnection) => {
    try {
      await api.updateDirectoryConnection(connection.id, {
        sync_enabled: !connection.sync_enabled,
      })
      await loadDirectoryConnections()
    } catch (error) {
      toast.error('Failed to update sync settings')
    }
  }

  const handlePromotePerson = async (role: 'admin' | 'contributor' | 'viewer') => {
    if (!personToPromote) return
    setPromotingPerson(true)
    try {
      const result = await api.promoteDirectoryPerson(personToPromote.id, role)
      if (result.success) {
        toast.success(`${personToPromote.email} promoted to GRC ${role}`)
        setShowPromoteModal(false)
        setPersonToPromote(null)
        await loadDirectoryPeople()
      } else {
        toast.error(result.message || 'Failed to promote person')
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to promote person')
    } finally {
      setPromotingPerson(false)
    }
  }

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'google':
        return (
          <svg className="h-4 w-4" viewBox="0 0 24 24">
            <path fill="#EA4335" d="M5.26620003,9.76452941 C6.19878754,6.93863203 8.85444915,4.90909091 12,4.90909091 C13.6909091,4.90909091 15.2181818,5.50909091 16.4181818,6.49090909 L19.9090909,3 C17.7818182,1.14545455 15.0545455,0 12,0 C7.27006974,0 3.1977497,2.69829785 1.23999023,6.65002441 L5.26620003,9.76452941 Z"/>
            <path fill="#34A853" d="M16.0407269,18.0125889 C14.9509167,18.7163016 13.5660892,19.0909091 12,19.0909091 C8.86648613,19.0909091 6.21911939,17.076871 5.27698177,14.2678769 L1.23746264,17.3349879 C3.19279051,21.2936293 7.26500293,24 12,24 C14.9328362,24 17.7353462,22.9573905 19.834192,20.9995801 L16.0407269,18.0125889 Z"/>
            <path fill="#4A90E2" d="M19.834192,20.9995801 C22.0291676,18.9520994 23.4545455,15.903663 23.4545455,12 C23.4545455,11.2909091 23.3454545,10.5272727 23.1818182,9.81818182 L12,9.81818182 L12,14.4545455 L18.4363636,14.4545455 C18.1187732,16.013626 17.2662994,17.2212117 16.0407269,18.0125889 L19.834192,20.9995801 Z"/>
            <path fill="#FBBC05" d="M5.27698177,14.2678769 C5.03832634,13.556323 4.90909091,12.7937589 4.90909091,12 C4.90909091,11.2182781 5.03443647,10.4668121 5.26620003,9.76452941 L1.23999023,6.65002441 C0.43658717,8.26043162 0,10.0753848 0,12 C0,13.9195484 0.444780743,15.7301709 1.23746264,17.3349879 L5.27698177,14.2678769 Z"/>
          </svg>
        )
      case 'entra':
        return (
          <svg className="h-4 w-4" viewBox="0 0 23 23">
            <path fill="#f35325" d="M1 1h10v10H1z"/>
            <path fill="#81bc06" d="M12 1h10v10H12z"/>
            <path fill="#05a6f0" d="M1 12h10v10H1z"/>
            <path fill="#ffba08" d="M12 12h10v10H12z"/>
          </svg>
        )
      case 'csv':
        return <Upload className="h-4 w-4 text-gray-500" />
      default:
        return <Users className="h-4 w-4 text-gray-500" />
    }
  }

  const getSourceLabel = (source: string) => {
    switch (source) {
      case 'google': return 'Google Workspace'
      case 'entra': return 'Microsoft Entra'
      case 'csv': return 'CSV Import'
      case 'manual': return 'Manual'
      default: return source
    }
  }

  // ============= Cloud Integration Handlers =============

  const handleSync = async (id: string) => {
    setSyncingId(id)
    setSyncResult(null)
    try {
      const response = await api.syncIntegration(id)
      setSyncResult({
        id,
        records: response.records_synced || 0,
        evidence: response.evidence_created || 0,
        error: response.error,
      })
      await loadIntegrations()
    } catch (error) {
      console.error('Sync failed:', error)
      setSyncResult({
        id,
        records: 0,
        evidence: 0,
        error: error instanceof Error ? error.message : 'Sync failed',
      })
    } finally {
      setSyncingId(null)
    }
  }

  const handleTest = async (id: string) => {
    const integration = integrations.find(i => i.id === id)
    setTestingId(id)
    try {
      const response = await api.testIntegration(id) as any
      if (response.success) {
        // For Azure integrations, show the preview modal
        if (integration?.type === 'azure' && response.preview) {
          setPreviewData(response)
          setPreviewIntegrationId(id)
          setShowPreviewModal(true)
          toast.success('Connected to Azure - review the preview')
        } else {
          toast.success('Connection successful!')
        }
      } else {
        toast.error(response.error || response.message || 'Connection failed')
      }
      await loadIntegrations()
    } catch (error) {
      console.error('Test failed:', error)
      toast.error('Connection test failed')
    } finally {
      setTestingId(null)
    }
  }

  const handleSyncFromPreview = async () => {
    if (!previewIntegrationId) return
    setShowPreviewModal(false)
    await handleSync(previewIntegrationId)
    setPreviewData(null)
    setPreviewIntegrationId(null)
  }

  const handleDeleteIntegration = async (id: string) => {
    const confirmed = await confirmDialog({
      title: 'Delete Integration',
      message: 'Are you sure you want to delete this integration?',
      type: 'danger',
      confirmText: 'Delete',
    })
    if (!confirmed) return
    try {
      await api.deleteIntegration(id)
      toast.success('Integration deleted successfully')
      await loadIntegrations()
    } catch (error) {
      console.error('Delete failed:', error)
      toast.error('Failed to delete integration')
    }
  }

  const getStatusIcon = (integration: Integration) => {
    if (integration.last_sync_status === 'error') {
      return <XCircle className="h-5 w-5 text-red-500" />
    }
    if (integration.enabled) {
      return <CheckCircle className="h-5 w-5 text-green-500" />
    }
    return <AlertCircle className="h-5 w-5 text-gray-400" />
  }

  // ============= Webhook Handlers =============

  const handleCreateWebhook = async () => {
    if (!webhookForm.name || !webhookForm.url || webhookForm.events.length === 0) {
      toast.error('Please fill in all required fields')
      return
    }
    setWebhookSaving(true)
    try {
      const res = await api.createWebhook({
        name: webhookForm.name,
        description: webhookForm.description || undefined,
        url: webhookForm.url,
        secret: webhookForm.secret || undefined,
        auth_type: webhookForm.auth_type,
        events: webhookForm.events,
        enabled: webhookForm.enabled,
      })
      if (res.success) {
        toast.success('Webhook created successfully')
        setShowWebhookModal(false)
        resetWebhookForm()
        await loadWebhooks()
      } else {
        toast.error('Failed to create webhook')
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to create webhook')
    } finally {
      setWebhookSaving(false)
    }
  }

  const handleUpdateWebhook = async () => {
    if (!editingWebhook) return
    setWebhookSaving(true)
    try {
      const res = await api.updateWebhook(editingWebhook.id, {
        name: webhookForm.name,
        description: webhookForm.description || null,
        url: webhookForm.url,
        secret: webhookForm.secret || null,
        auth_type: webhookForm.auth_type,
        events: webhookForm.events,
        enabled: webhookForm.enabled,
      })
      if (res.success) {
        toast.success('Webhook updated successfully')
        setShowWebhookModal(false)
        setEditingWebhook(null)
        resetWebhookForm()
        await loadWebhooks()
      } else {
        toast.error('Failed to update webhook')
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to update webhook')
    } finally {
      setWebhookSaving(false)
    }
  }

  const handleDeleteWebhook = async (id: string) => {
    const confirmed = await confirmDialog({
      title: 'Delete Webhook',
      message: 'Are you sure you want to delete this webhook?',
      type: 'danger',
      confirmText: 'Delete',
    })
    if (!confirmed) return
    setDeletingWebhook(id)
    try {
      const res = await api.deleteWebhook(id)
      if (res.success) {
        toast.success('Webhook deleted')
        await loadWebhooks()
      } else {
        toast.error('Failed to delete webhook')
      }
    } catch {
      toast.error('Failed to delete webhook')
    } finally {
      setDeletingWebhook(null)
    }
  }

  const handleTestWebhook = async (id: string) => {
    setTestingWebhook(id)
    try {
      const res = await api.testWebhook(id)
      if (res.success) {
        toast.success('Test webhook sent successfully')
      } else {
        toast.error(res.error || 'Test webhook failed')
      }
    } catch (err: any) {
      toast.error(err.message || 'Test webhook failed')
    } finally {
      setTestingWebhook(null)
    }
  }

  const handleToggleWebhook = async (webhook: WebhookType) => {
    try {
      const res = await api.updateWebhook(webhook.id, { enabled: !webhook.enabled })
      if (res.success) {
        await loadWebhooks()
      }
    } catch {
      toast.error('Failed to update webhook')
    }
  }

  const openEditWebhook = (webhook: WebhookType) => {
    setEditingWebhook(webhook)
    setWebhookForm({
      name: webhook.name,
      description: webhook.description || '',
      url: webhook.url,
      secret: '',
      auth_type: webhook.auth_type,
      events: webhook.events,
      enabled: !!webhook.enabled,
    })
    setShowWebhookModal(true)
  }

  const resetWebhookForm = () => {
    setWebhookForm({
      name: '',
      description: '',
      url: '',
      secret: '',
      auth_type: 'hmac',
      events: [],
      enabled: true,
    })
    setEditingWebhook(null)
  }

  const toggleWebhookEvent = (eventType: string) => {
    setWebhookForm(prev => ({
      ...prev,
      events: prev.events.includes(eventType)
        ? prev.events.filter(e => e !== eventType)
        : [...prev.events, eventType],
    }))
  }

  // ============= Utility Functions =============

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'Never'
    return new Date(timestamp).toLocaleString()
  }

  // ============= Render =============

  const loading = (subTab === 'cloud' && cloudLoading) || (subTab === 'webhooks' && webhooksLoading) || (subTab === 'directory' && directoryLoading)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Integrations</h1>
          <p className="text-gray-500 mt-1">
            Connect external services and configure outgoing webhooks
          </p>
        </div>
        <div className="flex items-center gap-3">
          {subTab === 'cloud' && (
            <>
              <Link
                to="/help"
                state={{ section: 'integrations', article: 'aws-setup' }}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <HelpCircle className="h-4 w-4" />
                Setup Guide
              </Link>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                <Plus className="h-5 w-5" />
                Add Integration
              </button>
            </>
          )}
          {subTab === 'webhooks' && (
            <button
              onClick={() => {
                resetWebhookForm()
                setShowWebhookModal(true)
              }}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <Plus className="h-5 w-5" />
              Add Webhook
            </button>
          )}
          {subTab === 'directory' && (
            <button
              onClick={() => setShowCsvModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <Upload className="h-5 w-5" />
              Import CSV
            </button>
          )}
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          <button
            onClick={() => setSubTab('cloud')}
            className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              subTab === 'cloud'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Cloud className="h-4 w-4" />
            Cloud Integrations
            {integrations.length > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                {integrations.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setSubTab('directory')}
            className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              subTab === 'directory'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="h-4 w-4" />
            User Directory
            {directoryConnections.length > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                {directoryConnections.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setSubTab('webhooks')}
            className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              subTab === 'webhooks'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Webhook className="h-4 w-4" />
            Webhooks
            {webhooks.length > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                {webhooks.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setSubTab('atlassian')}
            className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              subTab === 'atlassian'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Link2 className="h-4 w-4" />
            Atlassian (Jira/Confluence)
          </button>
        </nav>
      </div>

      {/* Cloud Integrations Tab */}
      {subTab === 'cloud' && (
        <>
          {/* Help Banner for empty state */}
          {integrations.length === 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <HelpCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-blue-800 font-medium">Need help setting up integrations?</p>
                <p className="text-blue-700 text-sm mt-1">
                  Our setup guides walk you through creating the required credentials in AWS and Azure.
                </p>
                <div className="flex gap-4 mt-2">
                  <Link to="/help" state={{ section: 'integrations', article: 'aws-setup' }} className="text-sm text-blue-600 hover:underline font-medium">
                    AWS Setup Guide →
                  </Link>
                  <Link to="/help" state={{ section: 'integrations', article: 'azure-setup' }} className="text-sm text-blue-600 hover:underline font-medium">
                    Azure Setup Guide →
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Integration Cards */}
          {integrations.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <Cloud className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No integrations configured</h3>
              <p className="text-gray-500 mb-6">
                Connect your cloud providers to start collecting compliance evidence automatically
              </p>
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                <Plus className="h-5 w-5" />
                Add Your First Integration
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {integrations.map((integration) => {
                const provider = providerInfo[integration.type]
                const status = integration.last_sync_status === 'error' ? 'error' : integration.enabled ? 'active' : 'inactive'
                return (
                  <div
                    key={integration.id}
                    className="bg-white rounded-xl border border-gray-200 overflow-hidden"
                  >
                    <div className={`h-2 ${provider.color}`} />
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${provider.color} bg-opacity-10`}>
                            <Cloud className={`h-6 w-6 ${provider.color.replace('bg-', 'text-')}`} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{integration.name}</h3>
                            <p className="text-sm text-gray-500">{provider.name}</p>
                          </div>
                        </div>
                        {getStatusIcon(integration)}
                      </div>

                      <div className="space-y-2 text-sm mb-4">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Status</span>
                          <span className={`font-medium capitalize ${
                            status === 'active' ? 'text-green-600' :
                            status === 'error' ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            {status}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Last Sync</span>
                          <span className="text-gray-900">{formatDate(integration.last_sync_at)}</span>
                        </div>
                        {integration.last_sync_status && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Sync Result</span>
                            <span className={`font-medium ${
                              integration.last_sync_status === 'success' ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {integration.last_sync_status}
                              {integration.last_sync_records ? ` (${integration.last_sync_records} records)` : ''}
                            </span>
                          </div>
                        )}
                        {syncResult && syncResult.id === integration.id && (
                          <div className={`p-2 rounded-lg text-xs ${syncResult.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                            {syncResult.error ? (
                              <span>{syncResult.error}</span>
                            ) : (
                              <span>Collected {syncResult.records} records, created {syncResult.evidence} evidence items</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Data types collected */}
                      <div className="mb-4">
                        <p className="text-xs text-gray-500 mb-2">Evidence collected:</p>
                        <div className="flex flex-wrap gap-1">
                          {provider.dataTypes.slice(0, 3).map((dt) => (
                            <span key={dt.name} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                              <dt.icon className="h-3 w-3" />
                              {dt.name.split(' ')[0]}
                            </span>
                          ))}
                          {provider.dataTypes.length > 3 && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                              +{provider.dataTypes.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pt-4 border-t border-gray-100">
                        <button
                          onClick={() => handleSync(integration.id)}
                          disabled={syncingId === integration.id}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg disabled:opacity-50"
                        >
                          <RefreshCw className={`h-4 w-4 ${syncingId === integration.id ? 'animate-spin' : ''}`} />
                          Sync
                        </button>
                        <button
                          onClick={() => handleTest(integration.id)}
                          disabled={testingId === integration.id}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg disabled:opacity-50"
                        >
                          {testingId === integration.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <ExternalLink className="h-4 w-4" />
                          )}
                          Test
                        </button>
                        <button
                          onClick={() => {
                            setSelectedIntegration(integration)
                            setShowConfigModal(true)
                          }}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg"
                        >
                          <Settings className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteIntegration(integration.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}

      {/* Webhooks Tab */}
      {subTab === 'webhooks' && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-6 border-b flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Webhook className="h-5 w-5" />
                Outgoing Webhooks
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Send notifications to external systems when events occur
              </p>
            </div>
          </div>
          <div className="divide-y">
            {webhooks.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Webhook className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">No webhooks configured</p>
                <p className="text-xs mt-1">
                  Add a webhook to send notifications to external systems
                </p>
                <button
                  onClick={() => {
                    resetWebhookForm()
                    setShowWebhookModal(true)
                  }}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  <Plus className="h-5 w-5" />
                  Add Your First Webhook
                </button>
              </div>
            ) : (
              webhooks.map((webhook) => (
                <div key={webhook.id} className="p-4 flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-block w-2 h-2 rounded-full ${
                          webhook.enabled ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      />
                      <span className="font-medium text-gray-900">{webhook.name}</span>
                      {webhook.description && (
                        <span className="text-sm text-gray-500">- {webhook.description}</span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <ExternalLink className="h-3 w-3" />
                        {webhook.url.length > 50 ? `${webhook.url.slice(0, 50)}...` : webhook.url}
                      </span>
                      <span>{webhook.events.length} event(s)</span>
                      <span className="text-green-600">{webhook.success_count} sent</span>
                      {webhook.failure_count > 0 && (
                        <span className="text-red-600">{webhook.failure_count} failed</span>
                      )}
                      <span>Last: {formatDate(webhook.last_triggered_at)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleWebhook(webhook)}
                      className={`text-xs px-2 py-1 rounded ${
                        webhook.enabled
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {webhook.enabled ? 'Enabled' : 'Disabled'}
                    </button>
                    <button
                      onClick={() => handleTestWebhook(webhook.id)}
                      disabled={testingWebhook === webhook.id}
                      className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded"
                      title="Send test event"
                    >
                      {testingWebhook === webhook.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => openEditWebhook(webhook)}
                      className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-gray-100 rounded"
                      title="Edit webhook"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteWebhook(webhook.id)}
                      disabled={deletingWebhook === webhook.id}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded"
                      title="Delete webhook"
                    >
                      {deletingWebhook === webhook.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Directory Tab */}
      {subTab === 'directory' && (
        <div className="space-y-6">
          {/* Provider Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* CSV Import Card */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="h-2 bg-gray-500" />
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-gray-100">
                    <Upload className="h-6 w-6 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">CSV Import</h3>
                    <p className="text-sm text-gray-500">One-time bulk imports</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Import users from a CSV file. Great for initial setup or periodic bulk updates.
                </p>
                <button
                  onClick={() => setShowCsvModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
                >
                  <Upload className="h-4 w-4" />
                  Import CSV
                </button>
              </div>
            </div>

            {/* Google Workspace Card */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="h-2 bg-red-500" />
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-red-50">
                    <svg className="h-6 w-6" viewBox="0 0 24 24">
                      <path fill="#EA4335" d="M5.26620003,9.76452941 C6.19878754,6.93863203 8.85444915,4.90909091 12,4.90909091 C13.6909091,4.90909091 15.2181818,5.50909091 16.4181818,6.49090909 L19.9090909,3 C17.7818182,1.14545455 15.0545455,0 12,0 C7.27006974,0 3.1977497,2.69829785 1.23999023,6.65002441 L5.26620003,9.76452941 Z"/>
                      <path fill="#34A853" d="M16.0407269,18.0125889 C14.9509167,18.7163016 13.5660892,19.0909091 12,19.0909091 C8.86648613,19.0909091 6.21911939,17.076871 5.27698177,14.2678769 L1.23746264,17.3349879 C3.19279051,21.2936293 7.26500293,24 12,24 C14.9328362,24 17.7353462,22.9573905 19.834192,20.9995801 L16.0407269,18.0125889 Z"/>
                      <path fill="#4A90E2" d="M19.834192,20.9995801 C22.0291676,18.9520994 23.4545455,15.903663 23.4545455,12 C23.4545455,11.2909091 23.3454545,10.5272727 23.1818182,9.81818182 L12,9.81818182 L12,14.4545455 L18.4363636,14.4545455 C18.1187732,16.013626 17.2662994,17.2212117 16.0407269,18.0125889 L19.834192,20.9995801 Z"/>
                      <path fill="#FBBC05" d="M5.27698177,14.2678769 C5.03832634,13.556323 4.90909091,12.7937589 4.90909091,12 C4.90909091,11.2182781 5.03443647,10.4668121 5.26620003,9.76452941 L1.23999023,6.65002441 C0.43658717,8.26043162 0,10.0753848 0,12 C0,13.9195484 0.444780743,15.7301709 1.23746264,17.3349879 L5.27698177,14.2678769 Z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Google Workspace</h3>
                    <p className="text-sm text-gray-500">Sync users automatically</p>
                  </div>
                </div>
                {(() => {
                  const googleConnection = directoryConnections.find(c => c.provider === 'google')
                  if (googleConnection) {
                    return (
                      <>
                        <div className="space-y-2 text-sm mb-4">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Status</span>
                            <span className={`flex items-center gap-1 ${googleConnection.status === 'active' ? 'text-green-600' : googleConnection.status === 'error' ? 'text-red-600' : 'text-gray-600'}`}>
                              {googleConnection.status === 'active' ? <CheckCircle className="h-3 w-3" /> : googleConnection.status === 'error' ? <XCircle className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                              {googleConnection.status}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Last Sync</span>
                            <span className="text-gray-900">{formatDate(googleConnection.last_sync_at)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Auto-sync</span>
                            <button
                              onClick={() => handleToggleDirectorySync(googleConnection)}
                              className={`text-xs px-2 py-0.5 rounded ${googleConnection.sync_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
                            >
                              {googleConnection.sync_enabled ? 'Enabled' : 'Disabled'}
                            </button>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleDirectorySync(googleConnection)}
                            disabled={syncingDirectory === googleConnection.id}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50 text-sm"
                          >
                            <RefreshCw className={`h-4 w-4 ${syncingDirectory === googleConnection.id ? 'animate-spin' : ''}`} />
                            Sync Now
                          </button>
                          <button
                            onClick={() => handleDisconnectDirectory(googleConnection)}
                            className="px-3 py-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                            title="Disconnect"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </>
                    )
                  }
                  return (
                    <>
                      <p className="text-sm text-gray-600 mb-4">
                        Connect Google Workspace to sync users, groups, and organizational units.
                      </p>
                      <button
                        onClick={() => handleConnectDirectory('google')}
                        disabled={connectingProvider === 'google'}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50"
                      >
                        {connectingProvider === 'google' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
                        Connect
                      </button>
                    </>
                  )
                })()}
              </div>
            </div>

            {/* Microsoft Entra Card */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="h-2 bg-blue-500" />
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-blue-50">
                    <svg className="h-6 w-6" viewBox="0 0 23 23">
                      <path fill="#f35325" d="M1 1h10v10H1z"/>
                      <path fill="#81bc06" d="M12 1h10v10H12z"/>
                      <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                      <path fill="#ffba08" d="M12 12h10v10H12z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Microsoft Entra ID</h3>
                    <p className="text-sm text-gray-500">Sync users automatically</p>
                  </div>
                </div>
                {(() => {
                  const entraConnection = directoryConnections.find(c => c.provider === 'entra')
                  if (entraConnection) {
                    return (
                      <>
                        <div className="space-y-2 text-sm mb-4">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Status</span>
                            <span className={`flex items-center gap-1 ${entraConnection.status === 'active' ? 'text-green-600' : entraConnection.status === 'error' ? 'text-red-600' : 'text-gray-600'}`}>
                              {entraConnection.status === 'active' ? <CheckCircle className="h-3 w-3" /> : entraConnection.status === 'error' ? <XCircle className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                              {entraConnection.status}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Last Sync</span>
                            <span className="text-gray-900">{formatDate(entraConnection.last_sync_at)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Auto-sync</span>
                            <button
                              onClick={() => handleToggleDirectorySync(entraConnection)}
                              className={`text-xs px-2 py-0.5 rounded ${entraConnection.sync_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
                            >
                              {entraConnection.sync_enabled ? 'Enabled' : 'Disabled'}
                            </button>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleDirectorySync(entraConnection)}
                            disabled={syncingDirectory === entraConnection.id}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50 text-sm"
                          >
                            <RefreshCw className={`h-4 w-4 ${syncingDirectory === entraConnection.id ? 'animate-spin' : ''}`} />
                            Sync Now
                          </button>
                          <button
                            onClick={() => handleDisconnectDirectory(entraConnection)}
                            className="px-3 py-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                            title="Disconnect"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </>
                    )
                  }
                  return (
                    <>
                      <p className="text-sm text-gray-600 mb-4">
                        Connect Microsoft Entra ID (Azure AD) to sync users and groups.
                      </p>
                      <button
                        onClick={() => handleConnectDirectory('entra')}
                        disabled={connectingProvider === 'entra'}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
                      >
                        {connectingProvider === 'entra' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
                        Connect
                      </button>
                    </>
                  )
                })()}
              </div>
            </div>
          </div>

          {/* Directory People */}
          <div className="bg-white rounded-xl border border-gray-200">
            <button
              onClick={() => setShowPeople(!showPeople)}
              className="w-full flex items-center justify-between p-4 text-left"
            >
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-gray-400" />
                <h3 className="font-semibold text-gray-900">Directory People</h3>
                {peopleStats && (
                  <span className="text-sm text-gray-500">
                    ({peopleStats.by_status.reduce((sum, s) => sum + s.count, 0)} people)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4">
                {peopleStats && (
                  <div className="flex items-center gap-3 text-sm">
                    {peopleStats.by_source.map((s) => (
                      <span key={s.directory_source} className="flex items-center gap-1 text-gray-500">
                        {getSourceIcon(s.directory_source)}
                        {s.count}
                      </span>
                    ))}
                  </div>
                )}
                {showPeople ? (
                  <ChevronUp className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </button>
            {showPeople && (
              <div className="border-t">
                {/* Filters */}
                <div className="p-4 bg-gray-50 border-b flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Search className="h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by name or email..."
                      value={peopleFilter.search}
                      onChange={(e) => setPeopleFilter({ ...peopleFilter, search: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && loadDirectoryPeople()}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-64"
                    />
                  </div>
                  <select
                    value={peopleFilter.source}
                    onChange={(e) => setPeopleFilter({ ...peopleFilter, source: e.target.value })}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Sources</option>
                    <option value="csv">CSV Import</option>
                    <option value="google">Google Workspace</option>
                    <option value="entra">Microsoft Entra</option>
                    <option value="manual">Manual</option>
                  </select>
                  <select
                    value={peopleFilter.department}
                    onChange={(e) => setPeopleFilter({ ...peopleFilter, department: e.target.value })}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Departments</option>
                    {departments.map((d) => (
                      <option key={d.department} value={d.department}>
                        {d.department} ({d.person_count})
                      </option>
                    ))}
                  </select>
                  {(peopleFilter.search || peopleFilter.source || peopleFilter.department) && (
                    <button
                      onClick={() => setPeopleFilter({ search: '', source: '', department: '' })}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      Clear filters
                    </button>
                  )}
                </div>

                {/* People Table */}
                {peopleLoading ? (
                  <div className="p-6 text-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary-600 mx-auto" />
                  </div>
                ) : directoryPeople.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    <Users className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>No directory people found</p>
                    <p className="text-sm mt-1">Import users via CSV or connect a directory provider</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Person</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Synced</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {directoryPeople.map((person) => (
                          <tr key={person.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {person.first_name || person.last_name
                                    ? `${person.first_name || ''} ${person.last_name || ''}`.trim()
                                    : '(No name)'}
                                </div>
                                <div className="flex items-center gap-1 text-sm text-gray-500">
                                  <Mail className="h-3 w-3" />
                                  {person.email}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {person.department ? (
                                <span className="flex items-center gap-1 text-sm text-gray-600">
                                  <Building2 className="h-3 w-3" />
                                  {person.department}
                                </span>
                              ) : (
                                <span className="text-sm text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                              {person.title || <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="flex items-center gap-1.5 text-sm text-gray-600">
                                {getSourceIcon(person.directory_source)}
                                {getSourceLabel(person.directory_source)}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {person.promoted_to_user_id ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                  <ArrowUpCircle className="h-3 w-3" />
                                  GRC User
                                </span>
                              ) : person.status === 'active' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                  <CheckCircle className="h-3 w-3" />
                                  Active
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                                  <XCircle className="h-3 w-3" />
                                  Inactive
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                              {person.last_synced_at
                                ? formatDate(person.last_synced_at)
                                : formatDate(person.created_at)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right">
                              {!person.promoted_to_user_id && (
                                <button
                                  onClick={() => {
                                    setPersonToPromote(person)
                                    setShowPromoteModal(true)
                                  }}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded"
                                  title="Promote to GRC User"
                                >
                                  <ArrowUpCircle className="h-3.5 w-3.5" />
                                  Promote
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sync History */}
          <div className="bg-white rounded-xl border border-gray-200">
            <button
              onClick={() => setShowSyncLogs(!showSyncLogs)}
              className="w-full flex items-center justify-between p-4 text-left"
            >
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-gray-400" />
                <h3 className="font-semibold text-gray-900">Sync History</h3>
                <span className="text-sm text-gray-500">({syncLogs.length} records)</span>
              </div>
              {showSyncLogs ? (
                <ChevronUp className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              )}
            </button>
            {showSyncLogs && (
              <div className="border-t divide-y">
                {syncLogs.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    <Clock className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>No sync history yet</p>
                  </div>
                ) : (
                  syncLogs.map((log) => (
                    <div key={log.id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          log.status === 'completed' ? 'bg-green-100' : log.status === 'failed' ? 'bg-red-100' : 'bg-yellow-100'
                        }`}>
                          {log.status === 'completed' ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : log.status === 'failed' ? (
                            <XCircle className="h-4 w-4 text-red-600" />
                          ) : (
                            <Loader2 className="h-4 w-4 text-yellow-600 animate-spin" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 capitalize">
                            {log.sync_type.replace('_', ' ')} sync
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDate(log.started_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        {log.status === 'completed' && (
                          <>
                            <span className="flex items-center gap-1 text-green-600">
                              <UserPlus className="h-3 w-3" />
                              {log.users_created} created
                            </span>
                            <span className="flex items-center gap-1 text-blue-600">
                              <Users className="h-3 w-3" />
                              {log.users_updated} updated
                            </span>
                          </>
                        )}
                        {log.status === 'failed' && log.errors.length > 0 && (
                          <span className="text-red-600 text-xs">
                            {log.errors[0]}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Atlassian Tab */}
      {subTab === 'atlassian' && <AtlassianIntegration />}

      {/* Add Cloud Integration Modal */}
      {showAddModal && (
        <AddIntegrationModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false)
            loadIntegrations()
          }}
        />
      )}

      {/* Cloud Config Modal */}
      {showConfigModal && selectedIntegration && (
        <ConfigModal
          integration={selectedIntegration}
          onClose={() => {
            setShowConfigModal(false)
            setSelectedIntegration(null)
          }}
          onSuccess={() => {
            setShowConfigModal(false)
            setSelectedIntegration(null)
            loadIntegrations()
          }}
        />
      )}

      {/* Azure Preview Modal */}
      {showPreviewModal && previewData && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowPreviewModal(false)} />
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
              <div className="bg-white px-6 py-5">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">Azure Connection Preview</h3>
                    {previewData.connection && (
                      <p className="text-sm text-gray-500 mt-1">
                        Connected to: <span className="font-medium">{previewData.connection.subscriptionName}</span>
                        <span className="text-gray-400 ml-2">({previewData.connection.state})</span>
                      </p>
                    )}
                  </div>
                  <button onClick={() => setShowPreviewModal(false)} className="text-gray-400 hover:text-gray-500">
                    <X className="h-6 w-6" />
                  </button>
                </div>

                {/* Resources Found Table */}
                {previewData.preview && previewData.preview.length > 0 ? (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Resources Found</h4>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resource Type</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Count</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Internal Controls</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {previewData.preview.map((item: any, idx: number) => (
                            <tr key={idx}>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.name}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{item.count}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {item.mapsToControls?.map((c: any) => (
                                  <span key={c.control_number} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mr-1">
                                    {c.control_number}
                                  </span>
                                ))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="mb-6 p-4 bg-yellow-50 rounded-lg">
                    <p className="text-sm text-yellow-800">No resources found in this Azure subscription.</p>
                  </div>
                )}

                {/* Summary */}
                {previewData.totals && (
                  <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      Syncing will create <span className="font-semibold">{previewData.totals.evidenceRecords}</span> evidence records,
                      updating <span className="font-semibold">{previewData.totals.internalControlsAffected}</span> internal controls,
                      which satisfy <span className="font-semibold">{previewData.totals.frameworkControlsSatisfied}</span> framework controls.
                    </p>
                  </div>
                )}

                {/* Errors */}
                {previewData.errors && previewData.errors.length > 0 && (
                  <div className="mb-6 p-4 bg-red-50 rounded-lg">
                    <h4 className="text-sm font-medium text-red-800 mb-2">Collection Errors</h4>
                    <ul className="text-sm text-red-700 list-disc list-inside">
                      {previewData.errors.map((err: string, idx: number) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    onClick={() => setShowPreviewModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSyncFromPreview}
                    disabled={!previewData.preview || previewData.preview.length === 0}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Proceed to Sync
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Webhook Modal */}
      {showWebhookModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => {
              setShowWebhookModal(false)
              resetWebhookForm()
            }} />
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {editingWebhook ? 'Edit Webhook' : 'Create Webhook'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowWebhookModal(false)
                      resetWebhookForm()
                    }}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={webhookForm.name}
                      onChange={(e) => setWebhookForm({ ...webhookForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="My Webhook"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <input
                      type="text"
                      value={webhookForm.description}
                      onChange={(e) => setWebhookForm({ ...webhookForm, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Optional description"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL *</label>
                    <input
                      type="url"
                      value={webhookForm.url}
                      onChange={(e) => setWebhookForm({ ...webhookForm, url: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="https://example.com/webhook"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Authentication</label>
                      <select
                        value={webhookForm.auth_type}
                        onChange={(e) => setWebhookForm({ ...webhookForm, auth_type: e.target.value as 'none' | 'hmac' | 'bearer' | 'basic' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="hmac">HMAC Signature</option>
                        <option value="bearer">Bearer Token</option>
                        <option value="basic">Basic Auth</option>
                        <option value="none">None</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {webhookForm.auth_type === 'hmac' ? 'Secret' :
                         webhookForm.auth_type === 'bearer' ? 'Token' :
                         webhookForm.auth_type === 'basic' ? 'user:pass' : 'N/A'}
                      </label>
                      <input
                        type="password"
                        value={webhookForm.secret}
                        onChange={(e) => setWebhookForm({ ...webhookForm, secret: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder={editingWebhook ? '(unchanged)' : 'Secret value'}
                        disabled={webhookForm.auth_type === 'none'}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Events *</label>
                    <div className="border rounded-lg p-3 max-h-48 overflow-y-auto">
                      {webhookEvents.length === 0 ? (
                        <p className="text-sm text-gray-500">Loading events...</p>
                      ) : (
                        <div className="space-y-2">
                          {webhookEvents.map((event) => (
                            <label
                              key={event.type}
                              className="flex items-start gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                            >
                              <input
                                type="checkbox"
                                checked={webhookForm.events.includes(event.type)}
                                onChange={() => toggleWebhookEvent(event.type)}
                                className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <div>
                                <span className="text-sm font-medium text-gray-900">
                                  {event.type.replace(/_/g, ' ')}
                                </span>
                                <p className="text-xs text-gray-500">{event.description}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    {webhookForm.events.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        {webhookForm.events.length} event(s) selected
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="webhook-enabled"
                      checked={webhookForm.enabled}
                      onChange={(e) => setWebhookForm({ ...webhookForm, enabled: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor="webhook-enabled" className="text-sm text-gray-700">
                      Enable webhook immediately
                    </label>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  onClick={editingWebhook ? handleUpdateWebhook : handleCreateWebhook}
                  disabled={webhookSaving}
                  className="inline-flex w-full justify-center rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 sm:ml-3 sm:w-auto disabled:opacity-50"
                >
                  {webhookSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {editingWebhook ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {editingWebhook ? 'Update Webhook' : 'Create Webhook'}
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowWebhookModal(false)
                    resetWebhookForm()
                  }}
                  className="mt-3 inline-flex w-full justify-center rounded-lg bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      <CsvImportModal
        isOpen={showCsvModal}
        onClose={() => setShowCsvModal(false)}
        onSuccess={() => {
          setShowCsvModal(false)
          loadDirectoryConnections()
          loadDirectoryPeople()
        }}
      />

      {/* Promote Person Modal */}
      {showPromoteModal && personToPromote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Promote to GRC User</h2>
                <button
                  onClick={() => {
                    setShowPromoteModal(false)
                    setPersonToPromote(null)
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Promote <span className="font-medium text-gray-900">{personToPromote.email}</span> to a GRC platform user.
                They will receive login access to the platform.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800">
                  This will create a user account. The person will need to complete email verification to log in.
                </p>
              </div>
              <p className="text-sm font-medium text-gray-700 mb-2">Select a role:</p>
              <div className="space-y-2">
                <button
                  onClick={() => handlePromotePerson('viewer')}
                  disabled={promotingPerson}
                  className="w-full flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 text-left"
                >
                  <div>
                    <p className="font-medium text-gray-900">Viewer</p>
                    <p className="text-xs text-gray-500">Read-only access to dashboards and reports</p>
                  </div>
                  {promotingPerson ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronDown className="h-4 w-4 text-gray-400 rotate-[-90deg]" />}
                </button>
                <button
                  onClick={() => handlePromotePerson('contributor')}
                  disabled={promotingPerson}
                  className="w-full flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 text-left"
                >
                  <div>
                    <p className="font-medium text-gray-900">Contributor</p>
                    <p className="text-xs text-gray-500">Can edit controls, evidence, and assessments</p>
                  </div>
                  {promotingPerson ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronDown className="h-4 w-4 text-gray-400 rotate-[-90deg]" />}
                </button>
                <button
                  onClick={() => handlePromotePerson('admin')}
                  disabled={promotingPerson}
                  className="w-full flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 text-left"
                >
                  <div>
                    <p className="font-medium text-gray-900">Admin</p>
                    <p className="text-xs text-gray-500">Full access including user management and settings</p>
                  </div>
                  {promotingPerson ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronDown className="h-4 w-4 text-gray-400 rotate-[-90deg]" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OAuth Configuration Modal */}
      {showOAuthConfigModal && oauthConfigProvider && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Configure {oauthConfigProvider === 'google' ? 'Google Workspace' : 'Microsoft Entra ID'}
                </h2>
                <button
                  onClick={() => setShowOAuthConfigModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-2">Setup Instructions</h3>
                <p className="text-sm text-blue-800 mb-2">
                  {oauthConfigProvider === 'google' ? (
                    <>
                      1. Go to <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a><br />
                      2. Create an OAuth 2.0 Client ID (Web application)<br />
                      3. Add redirect URI: <code className="bg-blue-100 px-1 rounded text-xs">https://app.clarantis.com/api/directory/oauth/google/callback</code><br />
                      4. Enable the Admin SDK API
                    </>
                  ) : (
                    <>
                      1. Go to <a href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noopener noreferrer" className="underline">Azure App Registrations</a><br />
                      2. Register a new application<br />
                      3. Add redirect URI: <code className="bg-blue-100 px-1 rounded text-xs">https://app.clarantis.com/api/directory/oauth/entra/callback</code><br />
                      4. Grant User.Read.All and Group.Read.All permissions
                    </>
                  )}
                </p>
<a href="/help?article=user-directory-setup" className="text-blue-600 hover:underline text-sm">Full setup instructions →</a>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={oauthConfigForm.clientId}
                  onChange={(e) => setOAuthConfigForm({ ...oauthConfigForm, clientId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder={oauthConfigProvider === 'google' ? 'xxx.apps.googleusercontent.com' : 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Secret <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={oauthConfigForm.clientSecret}
                  onChange={(e) => setOAuthConfigForm({ ...oauthConfigForm, clientSecret: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter client secret"
                />
              </div>

              {oauthConfigProvider === 'entra' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Directory (Tenant) ID
                  </label>
                  <input
                    type="text"
                    value={oauthConfigForm.entraTenantId}
                    onChange={(e) => setOAuthConfigForm({ ...oauthConfigForm, entraTenantId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Leave blank for 'common' (multi-tenant)"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Your Azure AD tenant ID. Leave blank to allow any Microsoft account.
                  </p>
                </div>
              )}
            </div>

            <div className="p-6 border-t bg-gray-50 rounded-b-xl flex justify-end gap-3">
              <button
                onClick={() => setShowOAuthConfigModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveOAuthConfig}
                disabled={oauthConfigSaving || !oauthConfigForm.clientId || !oauthConfigForm.clientSecret}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
              >
                {oauthConfigSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Plug className="h-4 w-4" />
                    Connect & Authorize
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============= Cloud Integration Modals =============

function AddIntegrationModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void
  onSuccess: () => void
}) {
  const [step, setStep] = useState<'select' | 'configure'>('select')
  const [integrationType, setIntegrationType] = useState<'aws' | 'azure' | 'gcp' | null>(null)
  const [name, setName] = useState('')
  const [config, setConfig] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const toast = useToast()

  const configFields: Record<string, Array<{ key: string; label: string; type: string; placeholder?: string; options?: { value: string; label: string }[]; helpText?: string }>> = {
    aws: [
      { key: 'access_key_id', label: 'Access Key ID', type: 'text' },
      { key: 'secret_access_key', label: 'Secret Access Key', type: 'password' },
      { key: 'region', label: 'Region', type: 'select', placeholder: 'Select a region', options: AWS_REGIONS, helpText: 'Select "All Regions" for comprehensive multi-region scanning. IAM data is always collected globally.' },
    ],
    azure: [
      { key: 'tenant_id', label: 'Tenant ID', type: 'text' },
      { key: 'client_id', label: 'Client ID', type: 'text' },
      { key: 'client_secret', label: 'Client Secret', type: 'password' },
      { key: 'subscription_id', label: 'Subscription ID', type: 'text' },
    ],
    gcp: [
      { key: 'project_id', label: 'Project ID', type: 'text' },
      { key: 'service_account_key', label: 'Service Account Key (JSON)', type: 'textarea' },
    ],
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!integrationType || !name) return

    setSaving(true)
    try {
      await api.createIntegration({
        name,
        type: integrationType,
        config,
      })
      toast.success('Integration created successfully')
      onSuccess()
    } catch (error) {
      console.error('Failed to create integration:', error)
      toast.error('Failed to create integration')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {step === 'select' ? 'Add Cloud Integration' : `Configure ${providerInfo[integrationType!].name}`}
          </h2>
        </div>

        {step === 'select' ? (
          <div className="p-6 space-y-4">
            {(['aws', 'azure'] as const).map((p) => {
              const info = providerInfo[p]
              return (
                <button
                  key={p}
                  onClick={() => {
                    setIntegrationType(p)
                    setStep('configure')
                  }}
                  className="w-full flex items-start gap-4 p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors text-left"
                >
                  <div className={`p-3 rounded-lg ${info.color} bg-opacity-10`}>
                    <Cloud className={`h-6 w-6 ${info.color.replace('bg-', 'text-')}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{info.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{info.description}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {info.dataTypes.map((dt) => (
                        <span key={dt.name} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                          <dt.icon className="h-3 w-3" />
                          {dt.name}
                        </span>
                      ))}
                    </div>
                  </div>
                </button>
              )
            })}
            {/* GCP - Coming Soon */}
            <div className="w-full flex items-start gap-4 p-4 border border-gray-200 rounded-lg opacity-60 cursor-not-allowed">
              <div className={`p-3 rounded-lg ${providerInfo.gcp.color} bg-opacity-10`}>
                <Cloud className={`h-6 w-6 ${providerInfo.gcp.color.replace('bg-', 'text-')}`} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{providerInfo.gcp.name}</h3>
                <p className="text-sm text-gray-500 mt-1">Coming soon</p>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={onClose}
                className="w-full px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Integration Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={`My ${providerInfo[integrationType!].name} Integration`}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>

            {configFields[integrationType!].map((field) => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.label}
                </label>
                {field.type === 'select' && field.options ? (
                  <>
                    <select
                      value={config[field.key] || ''}
                      onChange={(e) => setConfig({ ...config, [field.key]: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                    >
                      <option value="">{field.placeholder || 'Select...'}</option>
                      {field.options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {field.helpText && (
                      <p className="text-xs text-gray-500 mt-1">{field.helpText}</p>
                    )}
                  </>
                ) : field.type === 'textarea' ? (
                  <textarea
                    value={config[field.key] || ''}
                    onChange={(e) => setConfig({ ...config, [field.key]: e.target.value })}
                    placeholder={field.placeholder}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                  />
                ) : (
                  <input
                    type={field.type}
                    value={config[field.key] || ''}
                    onChange={(e) => setConfig({ ...config, [field.key]: e.target.value })}
                    placeholder={field.placeholder}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                )}
              </div>
            ))}

            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setStep('select')}
                className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={saving || !name}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? 'Creating...' : 'Create Integration'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

function ConfigModal({
  integration,
  onClose,
  onSuccess,
}: {
  integration: Integration
  onClose: () => void
  onSuccess: () => void
}) {
  const [name, setName] = useState(integration.name)
  const [config, setConfig] = useState<Record<string, string>>(integration.config || {})
  const [saving, setSaving] = useState(false)
  const toast = useToast()

  const configFields: Record<string, Array<{ key: string; label: string; type: string; placeholder?: string; options?: { value: string; label: string }[]; helpText?: string }>> = {
    aws: [
      { key: 'access_key_id', label: 'Access Key ID', type: 'text' },
      { key: 'secret_access_key', label: 'Secret Access Key', type: 'password' },
      { key: 'region', label: 'Region', type: 'select', placeholder: 'Select a region', options: AWS_REGIONS, helpText: 'Select "All Regions" for multi-region scanning.' },
    ],
    azure: [
      { key: 'tenant_id', label: 'Tenant ID', type: 'text' },
      { key: 'client_id', label: 'Client ID', type: 'text' },
      { key: 'client_secret', label: 'Client Secret', type: 'password' },
      { key: 'subscription_id', label: 'Subscription ID', type: 'text' },
    ],
    gcp: [
      { key: 'project_id', label: 'Project ID', type: 'text' },
      { key: 'service_account_key', label: 'Service Account Key (JSON)', type: 'textarea' },
    ],
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      await api.updateIntegration(integration.id, { name, config })
      toast.success('Integration updated successfully')
      onSuccess()
    } catch (error) {
      console.error('Failed to update integration:', error)
      toast.error('Failed to update integration')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Configure {providerInfo[integration.type].name}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Integration Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>

          {configFields[integration.type].map((field) => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
              </label>
              {field.type === 'select' && field.options ? (
                <>
                  <select
                    value={config[field.key] || ''}
                    onChange={(e) => setConfig({ ...config, [field.key]: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                  >
                    <option value="">{field.placeholder || 'Select...'}</option>
                    {field.options.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {field.helpText && (
                    <p className="text-xs text-gray-500 mt-1">{field.helpText}</p>
                  )}
                </>
              ) : field.type === 'textarea' ? (
                <textarea
                  value={config[field.key] || ''}
                  onChange={(e) => setConfig({ ...config, [field.key]: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                />
              ) : (
                <input
                  type={field.type}
                  value={config[field.key] || ''}
                  onChange={(e) => setConfig({ ...config, [field.key]: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              )}
            </div>
          ))}

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
